import { Link, useNavigate } from 'react-router-dom'
import Button from '../../components/common/button'
import IconBox from '../../components/common/icon-box'
import MainNav from '../../components/homepage/main-nav'
import Layout from '../../components/layout/layout'
import { SubmitHandler, useForm } from 'react-hook-form'
import { ForgotPasswordInitDto } from '@ringo/shared'
import { FC, useEffect, useRef } from 'react'
import { useUser } from '../../common/hooks/auth/useUser'
import { useTranslation } from '../../useTranslation'
import { useMutation } from '@tanstack/react-query'
import ControlledInputText from '../../components/form/controlled-input-text'
import { object } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useValidationRules } from '../../common/validation-rules'
import InfoMessage from '../../components/common/info-message'
import { Validation } from '../../common/types'
import { useIamApi } from '../../common/api-context'
import { Captcha } from '../../components/common/captcha'
import HCaptcha from '@hcaptcha/react-hcaptcha'

type Props = {
    customTitle?: string
    customMessage?: string
    hideCancel?: boolean
    prefilledEmail?: string
}

const ForgotPassword: FC<Props> = ({ customMessage, customTitle, hideCancel, prefilledEmail }) => {
    const navigate = useNavigate()
    const { isAuthenticated } = useUser()
    const { t } = useTranslation()
    const validationRules = useValidationRules()
    const iamApi = useIamApi()
    const captchaRef = useRef<HCaptcha>(null)

    // Redirect if authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/')
        }
    }, [isAuthenticated, navigate])

    const validationObject: Validation<ForgotPasswordInitDto> = {
        email: validationRules.email,
    }

    const schema = object(validationObject)
    const { handleSubmit, control } = useForm<ForgotPasswordInitDto>({
        defaultValues: { email: prefilledEmail },
        resolver: yupResolver(schema as any),
    })

    const mutation = useMutation({
        mutationFn: (formData: ForgotPasswordInitDto) => {
            return iamApi.forgotPasswordInit(formData)
        },
    })

    const onSubmit: SubmitHandler<ForgotPasswordInitDto> = async (data) => {
        const { response } = await captchaRef.current!.execute({ async: true })

        mutation.mutate({ ...data, hcaptchaPasscode: response })
    }

    return (
        <Layout>
            <MainNav />
            <Captcha ref={captchaRef} />

            <div className="mx-auto my-16 w-3/6 max-tablet:w-2/3">
                <IconBox icon="key">
                    <form onSubmit={handleSubmit(onSubmit)} className="py-10 px-2">
                        <p className="font-bold text-xl text-left mb-3 leading-0 max-tablet:text-base">{customTitle ?? t('login.forgotPassword')}</p>
                        {mutation.isSuccess ? (
                            <InfoMessage type="success" label={t('passwordRequestSuccess')} icon="check" />
                        ) : (
                            <div>
                                {mutation.isError && <InfoMessage type="error" label={t('errorSendingEmail')} icon="xmark" />}
                                <p className="text-base font-light text-left mb-6 max-tablet:text-sm">
                                    {customMessage ?? t('login.forgotPassword.forgotPasswordConfirmationEmailText')}
                                </p>
                                <div>
                                    <ControlledInputText
                                        label={t('emailAddress')}
                                        control={control}
                                        name="email"
                                        disabled={!!prefilledEmail}
                                        dataCy="forgot-password-email"
                                    />
                                </div>
                                <div className="flex justify-center mt-7 gap-3">
                                    {!hideCancel && (
                                        <Link to="/auth/login">
                                            <Button icon="xmark" text={t('cancel')} type={'squareBlue'} preventSubmit dataCy="forgot-password-cancel" />
                                        </Link>
                                    )}
                                    <Button
                                        icon="envelope"
                                        text={t('login.forgotPassword.sendEmail')}
                                        type={'squareYellow'}
                                        isLoading={mutation.isPending}
                                        dataCy="forgot-password-submit"
                                    />
                                </div>
                            </div>
                        )}
                    </form>
                </IconBox>
            </div>
        </Layout>
    )
}

export default ForgotPassword
